import type { CanActivateFn } from '@angular/router';

/**
 * START A LAZY CHUNK DOWNLOADING WHILE THE ROUTE'S GUARDS ARE STILL TALKING
 * TO THE SERVER.
 *
 * ── THE SERIAL CHAIN THIS BREAKS ──────────────────────────────────────────
 * Angular fetches `loadComponent`'s chunk only once every `canActivate` guard
 * has passed. So a flag-gated lazy route costs:
 *
 *     guards (a round trip) → chunk download → component constructs →
 *     its first query goes out
 *
 * — and the middle two steps have nothing to say to each other. The flag read
 * does not need the code, and the code does not need the flag. On `/hubs` the
 * chunk is the largest single item in that chain, and all of it is downloaded
 * after the network has already gone quiet waiting for a guard to answer.
 *
 * This fires the same dynamic import the route will make and returns `true`
 * at once. The module registry dedupes, so when `loadComponent` asks moments
 * later it gets the download already in flight — or already finished.
 *
 * ── HOW ANGULAR ACTUALLY RUNS GUARDS, WHICH IS NOT IN ORDER ───────────────
 * ⚠ `canActivate` GUARDS RUN CONCURRENTLY. `runCanActivate` maps them to
 * observables and combines them with `prioritizedGuardValue`, which is a
 * `combineLatest` — every guard is subscribed at once. Only the DECISION is
 * prioritised: the first guard in the array whose answer is not `true` wins.
 *
 * This note is here because the first version of this file asserted the
 * opposite, in detail, and built its reasoning on it. The overlap works
 * either way — better, in fact, since the preload now runs alongside every
 * guard rather than only the ones after it — but two consequences follow that
 * do NOT match the intuition:
 *
 *   · Position in the `canActivate` array buys nothing at runtime. Putting
 *     this after `authGuard` does NOT stop an unauthenticated visitor's
 *     browser from starting the download; they will fetch the chunk and then
 *     be redirected to sign in. That is wasted bandwidth on a request that
 *     was already going to be redirected, and it is the honest price of the
 *     overlap. It is not an exposure — the chunk is static asset traffic that
 *     any visitor can request directly.
 *   · So put it after `authGuard` for READERS, because that is the order the
 *     list documents, not because the router enforces it.
 *
 * ── WHAT IT IS NOT ────────────────────────────────────────────────────────
 * NOT a guard in any meaningful sense: it decides nothing and can refuse
 * nothing. It is in the guard list because that is the only hook Angular
 * offers before the component is fetched.
 *
 * ⚠ IT MUST NEVER BE THE REASON A ROUTE OPENS OR CLOSES. It returns `true`
 * unconditionally and swallows its own failure: a chunk that fails to
 * download here will fail again at `loadComponent`, which is where the router
 * already knows how to report it. Rejecting here instead would turn a
 * transient network blip into a route that mysteriously refuses to open.
 */
export function warmChunk(load: () => Promise<unknown>): CanActivateFn {
  return () => {
    // Fire and forget. The `catch` is what keeps a failed preload from
    // surfacing as an unhandled rejection on a page that is otherwise fine.
    void load().catch(() => undefined);
    return true;
  };
}
